import {
  Box,
  Button,
  Card,
  Fade,
  IconButton,
  InputAdornment,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  approvedIcon,
  closeIcon,
  filterLightIcon,
  filterPrimaryIcon,
} from "assets/images";
import ConfirmModal from "components/ConfirmModal";
import CustomPagination from "components/customPagination";
import NoRecords from "components/NoRecords";
import { ExportToCsv } from "export-to-csv";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { IRoleModulePermission } from "store/role/types";
import { StudentWaitListedSuccessPayload } from "store/student/types";
import {
  ApproveWaitListedEnrolmentStatusPayload,
  ChangeEnrolmentStatusSuccessPayload,
} from "store/studentEnrolment/types";
import {
  CLEAR_TOOLTIP_TITLE,
  DATE_OF_BIRTH_FORMAT,
  DATE_VIEW_FORMAT,
  DEFAULT_PAGE_NO,
  DEFAULT_TEN_PAGE_SIZE,
  MAX_INT_VALUE,
} from "utils/constants/constant";
import {
  ENROLMENT_STATUS_CHANGED_SUCCESSFULLY,
  SELECT_MIN_ONE_FIELD,
} from "utils/constants/Messages";
import { AppPages } from "utils/enums/app-pages";
import { Order, OrderByOptions } from "utils/enums/sorting";
import {
  allowSearch,
  getPagePayloadValues,
  getPagePermissions,
  onTextFieldKeyPress,
  showLoader,
} from "utils/helper";
import {
  IEnhancedTablePropsForStudentWaitListed,
  IStudentWaitListContainerDispatch,
  IStudentWaitListed,
  IStudentWaitListedFilter,
  IStudentWaitListedKeys,
  IStudentWaitListedListMeta,
  IStudentWaitListedPayloadValues,
} from "utils/interfaces/student";
import { getUserSelector } from "store/auth/selector";
import { AuthState as AuthType } from "store/auth/types";
import { AuthState } from "store/rootReducer";
import StudentWaitlistedFilter from "./StudentWaitlistedFilter";

const EnhancedTableHead = (props: IEnhancedTablePropsForStudentWaitListed) => {
  const { order, orderBy, onRequestSort, fieldName, keyName, sxStyle } = props;
  const createSortHandler =
    (newOrderBy: keyof IStudentWaitListedKeys) =>
    (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, newOrderBy);
    };

  return (
    <TableCell
      key={keyName}
      align="left"
      onClick={createSortHandler(keyName)}
      sortDirection={orderBy === keyName ? order : false}
      sx={sxStyle}
      className="term-sorting"
    >
      {fieldName}
      <Box component="span" className="sorting-icon" />
    </TableCell>
  );
};

export type StudentsWaitListedProps = IStudentWaitListContainerDispatch & {
  callback: () => void;
};

const StudentsWaitListed: React.FC<StudentsWaitListedProps> = (props) => {
  const [studentWaitListedList, setStudentWaitListedList] =
    useState<IStudentWaitListed[]>();
  const [studentWaitListedListMeta, setStudentWaitListedListMeta] =
    useState<IStudentWaitListedListMeta>();
  const [searchText, setSearchText] = useState<string>("");
  const [page, setPage] = useState<string>(DEFAULT_TEN_PAGE_SIZE);
  const [pageNo, setPageNo] = useState<number>(DEFAULT_PAGE_NO);
  const [order, setOrder] = useState<Order>(OrderByOptions.Desc);
  const [orderBy, setOrderBy] =
    useState<keyof IStudentWaitListedKeys>("appliedOn");
  const [pagePermission, setPagePermission] = useState<IRoleModulePermission>();
  const [filterBox, setFilterBox] = useState<boolean>(false);
  const [filteredData, setFilteredData] = useState<IStudentWaitListedFilter>();
  const [appliedFilter, setAppliedFilter] = useState<boolean>(false);
  const [courseEnrollmentId, setCourseEnrollmentId] = useState<number>(0);

  const user = useSelector<AuthState, AuthType["user"]>(getUserSelector);
  const history = useHistory();

  const onStudentWaitListedSuccess = (
    response: StudentWaitListedSuccessPayload
  ) => {
    setStudentWaitListedList(response.studentWaitListedList);
    setStudentWaitListedListMeta(response.studentWaitListedListPagination);
  };

  const getStudentWaitListedList = (
    filterData?: IStudentWaitListedFilter,
    filtered?: boolean,
    pageNum?: number
  ) => {
    const { studentWaitListedRequest } = props;
    if (studentWaitListedRequest) {
      showLoader();
      const values: IStudentWaitListedPayloadValues = getPagePayloadValues(
        orderBy,
        order,
        pageNo,
        page
      );
      values.search = filtered ? "" : searchText.trim();

      if (filterData?.courseId) values.courseId = filterData?.courseId;
      if (filterData?.courseNo) values.courseNo = filterData?.courseNo.trim();
      if (filterData?.studentName)
        values.studentName = filterData?.studentName.trim();

      if (filterData) {
        setFilteredData(filterData);
        setAppliedFilter(true);

        if (pageNum) {
          values.pageNo = pageNum;
          setPageNo(pageNum);
        }
      } else {
        setFilteredData(undefined);
      }

      const payload = {
        values,
        callback: onStudentWaitListedSuccess,
      };

      studentWaitListedRequest(payload);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    const permission = getPagePermissions(
      user?.permissions,
      AppPages.StudentEnrolment
    );
    setPagePermission(permission);
    if (permission?.view) {
      if (filteredData && !appliedFilter)
        getStudentWaitListedList(filteredData, true, DEFAULT_PAGE_NO);
      else if (filteredData) getStudentWaitListedList(filteredData);
      else getStudentWaitListedList();
    } else {
      history.goBack();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageNo, order, orderBy]);

  const handleRequestSort = useCallback(
    (
      event: React.MouseEvent<unknown>,
      newOrderBy: keyof IStudentWaitListedKeys
    ) => {
      const isAsc = orderBy === newOrderBy && order === OrderByOptions.Asc;
      const toggledOrder = isAsc ? OrderByOptions.Desc : OrderByOptions.Asc;
      setOrder(toggledOrder);
      setOrderBy(newOrderBy);
    },
    [order, orderBy]
  );

  const handleChange = (event: SelectChangeEvent) => {
    setPage(event.target.value as string);
    setPageNo(DEFAULT_PAGE_NO);
  };

  const onPageChange = (e: React.ChangeEvent<unknown>, value: number) => {
    setPageNo(value);
  };

  const filterToggle = () => {
    document.body.classList.toggle("filter-open");
  };

  const handleFilterApplyClick = (formData: IStudentWaitListedFilter) => {
    if (!(formData.courseId || formData.courseNo || formData.studentName)) {
      toast.error(SELECT_MIN_ONE_FIELD);
    } else {
      setFilteredData(formData);
      setSearchText("");
      if (pageNo === DEFAULT_PAGE_NO) {
        getStudentWaitListedList(formData, true, DEFAULT_PAGE_NO);
      } else {
        setPageNo(DEFAULT_PAGE_NO);
      }
      setFilterBox(false);
      filterToggle();
    }
  };

  const handleStudentSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchText("");
    if (pageNo === DEFAULT_PAGE_NO)
      getStudentWaitListedList(filteredData, true);
    else setPageNo(DEFAULT_PAGE_NO);
  };

  const handleStudentSearchKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (allowSearch(e)) {
      if (pageNo === DEFAULT_PAGE_NO) getStudentWaitListedList(filteredData);
      else setPageNo(DEFAULT_PAGE_NO);
    }
    onTextFieldKeyPress(e, searchText?.length);
  };

  const getWaitListedStudentModifiedData = (list: IStudentWaitListed[]) => {
    const data = list?.map((student: IStudentWaitListed) => {
      return {
        "Student Name": student.studentName,
        "Date Of Birth": moment(student.dateOfBirth).format(
          DATE_OF_BIRTH_FORMAT
        ),
        Email: student.emailAddress,
        Phone: `="${student.phoneNumber}"`,
        "Applied On": moment(student.appliedDate).format(DATE_VIEW_FORMAT),
        "Course No": student.courseNo,
        "Course Name": student.courseName,
      };
    });

    return data;
  };

  const onExportWaitListedStudentListSuccess = (
    response: StudentWaitListedSuccessPayload
  ) => {
    const modifiedData = getWaitListedStudentModifiedData(
      response.studentWaitListedList
    );
    const csvFileHeaders = [
      "Student Name",
      "Date Of Birth",
      "Email",
      "Phone",
      "Applied On",
      "Course No",
      "Course Name",
    ];

    const options = {
      fieldSeparator: ",",
      quoteStrings: '"',
      decimalSeparator: ".",
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers: csvFileHeaders,
      filename: "WaitListedStudents",
    };

    const csvExporter = new ExportToCsv(options);
    csvExporter.generateCsv(modifiedData);
  };

  const getExportWaitListedStudents = () => {
    const { studentWaitListedRequest } = props;

    if (studentWaitListedRequest) {
      showLoader();

      const values: IStudentWaitListedPayloadValues = getPagePayloadValues(
        orderBy,
        order,
        DEFAULT_PAGE_NO,
        MAX_INT_VALUE
      );
      values.search = searchText.trim();

      if (filteredData?.courseId) values.courseId = filteredData?.courseId;
      if (filteredData?.courseNo)
        values.courseNo = filteredData?.courseNo.trim();
      if (filteredData?.studentName)
        values.studentName = filteredData?.studentName.trim();

      const payload = {
        values,
        callback: onExportWaitListedStudentListSuccess,
      };

      studentWaitListedRequest(payload);
    }
  };

  const onChangeEnrollmentStatusSuccess = (
    response: ChangeEnrolmentStatusSuccessPayload
  ) => {
    if (response.success) {
      const { callback } = props;
      toast.success(ENROLMENT_STATUS_CHANGED_SUCCESSFULLY);
      getStudentWaitListedList(filteredData);
      callback();
    }
    setCourseEnrollmentId(0);
  };

  const handleApprovedCourseEnrollment = () => {
    const { approveWaitListedEnrolmentStatusRequest } = props;

    if (approveWaitListedEnrolmentStatusRequest) {
      showLoader();
      const payload: ApproveWaitListedEnrolmentStatusPayload = {
        values: {
          id: courseEnrollmentId,
        },
        callback: onChangeEnrollmentStatusSuccess,
      };

      approveWaitListedEnrolmentStatusRequest(payload);
    }
  };

  return (
    <Card>
      <Box
        className="filter-overlay"
        onClick={() => {
          document.body.classList.toggle("filter-open");
          setFilterBox(false);
        }}
      />
      <Box className="table-card-header">
        <Box>
          <Typography variant="h3" className="heading">
            Students Waitlisted
          </Typography>
        </Box>
        <Box>
          <TextField
            id="search-box"
            variant="outlined"
            className="search-input"
            placeholder="Search"
            value={searchText}
            onChange={handleStudentSearch}
            onKeyDown={handleStudentSearchKeyDown}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  sx={{ position: "absolute", right: "0" }}
                  position="end"
                >
                  {searchText && (
                    <Tooltip
                      TransitionComponent={Fade}
                      TransitionProps={{ timeout: 600 }}
                      title={CLEAR_TOOLTIP_TITLE}
                      arrow
                    >
                      <IconButton edge="end" onClick={handleClearSearch}>
                        <img src={closeIcon} alt="close" />
                      </IconButton>
                    </Tooltip>
                  )}
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            className="btn-filter"
            onClick={() => {
              setFilterBox(true);
              document.body.classList.toggle("filter-open");
            }}
          >
            <img src={filterPrimaryIcon} alt="filter" className="default" />
            <img src={filterLightIcon} alt="filter" className="active" />
            <span>Filter</span>
          </Button>
          <Button
            variant="outlined"
            className="btn-export"
            onClick={getExportWaitListedStudents}
            disabled={!studentWaitListedListMeta?.totalCount}
          >
            Export
          </Button>
        </Box>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={(e) => handleRequestSort(e, "studentName")}
                fieldName="Student Name"
                keyName="studentName"
                sxStyle={{ width: "144px", minWidth: "125px" }}
              />
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={(e) => handleRequestSort(e, "dateOfBirth")}
                fieldName="Date Of Birth"
                keyName="dateOfBirth"
                sxStyle={{ width: "123px", minWidth: "105px" }}
              />
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={(e) => handleRequestSort(e, "mobile")}
                fieldName="Phone"
                keyName="mobile"
                sxStyle={{ width: "123px", minWidth: "105px" }}
              />
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={(e) => handleRequestSort(e, "email")}
                fieldName="Email"
                keyName="email"
                sxStyle={{ width: "200px", minWidth: "180px" }}
              />
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={(e) => handleRequestSort(e, "appliedOn")}
                fieldName="Enrolled On"
                keyName="appliedOn"
                sxStyle={{ width: "123px", minWidth: "105px" }}
              />
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={(e) => handleRequestSort(e, "courseNo")}
                fieldName="Course No"
                keyName="courseNo"
                sxStyle={{ width: "144px", minWidth: "144px" }}
              />
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={(e) => handleRequestSort(e, "courseName")}
                fieldName="Course"
                keyName="courseName"
                sxStyle={{ width: "268px", minWidth: "216px" }}
              />
              <TableCell align="center" sx={{ width: "80px" }}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {studentWaitListedList?.map((student) => (
              <TableRow key={student.id}>
                <TableCell>{student.studentName}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {moment(student.dateOfBirth).format(DATE_OF_BIRTH_FORMAT)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{student.phoneNumber}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" className="email">
                    {student.emailAddress}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {moment(student.appliedDate).format(DATE_VIEW_FORMAT)}
                  </Typography>
                </TableCell>
                <TableCell component="th" scope="student">
                  {student.courseNo}
                </TableCell>
                <TableCell>{student.courseName}</TableCell>
                <TableCell align="center">
                  <div className="table-actions">
                    <Tooltip
                      TransitionComponent={Fade}
                      TransitionProps={{ timeout: 600 }}
                      title="Approve"
                      arrow
                    >
                      <IconButton
                        disabled={!pagePermission?.edit}
                        onClick={() => setCourseEnrollmentId(student.id)}
                      >
                        <img src={approvedIcon} alt="Approved" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {studentWaitListedList && studentWaitListedList.length > 0 ? (
        <CustomPagination
          handleChange={handleChange}
          page={page}
          pageNo={pageNo}
          onPageChange={onPageChange}
          listMeta={studentWaitListedListMeta}
        />
      ) : (
        <NoRecords />
      )}

      {filterBox && (
        <StudentWaitlistedFilter
          filterBox={filterBox}
          setFilterBox={setFilterBox}
          handleApplyClick={handleFilterApplyClick}
          filteredData={filteredData}
          pageNo={pageNo}
          setPageNo={setPageNo}
          setFilteredData={setFilteredData}
          getStudentWaitListedList={getStudentWaitListedList}
          setAppliedFilter={setAppliedFilter}
        />
      )}

      {!!courseEnrollmentId && (
        <ConfirmModal
          handleSubmitConfirmationModalClose={() => setCourseEnrollmentId(0)}
          submitConfirmationMessage="Do you want to approve this student?"
          handleYesClick={handleApprovedCourseEnrollment}
        />
      )}
    </Card>
  );
};

export default StudentsWaitListed;
